/**
 * The ☰ menu: who you are, and the app-wide things that belong to nobody else.
 *
 * ## It renders unconditionally, and that is the point
 *
 * These controls first lived in the acting-as bar, because that bar already
 * existed — which is a reason to write code, not a reason for it to be there.
 * The bar hides itself when `/auth/me` does not answer, so a stale server took
 * sign-out down with it and left no way out of a session. App chrome must not
 * be contingent on a fetch: this asks the server who you are, and *shows what it
 * has* if the answer never comes.
 *
 * ## What it can offer, it offers; what it cannot, it omits
 *
 * No Firebase — no account section, no sign-out, because an unconfigured build
 * has no session to end. Same rule as the sign-in providers: a control that
 * fails when clicked is worse than absent.
 *
 * ## The switcher is here; the banner is not
 *
 * Picking an account to view is a setting, and settings live in a menu. Being
 * *in* that mode is not a setting — it is a state you must not be able to forget
 * you are in, so it stays a full-width banner that no menu can hide. The two
 * halves of acting-as are deliberately not in the same place.
 *
 * ## Which feature rows appear is the brand's answer
 *
 * Account, View as, Backend and Sign out are **chrome** — they are how
 * you get into and out of an account, and no brand may remove the way out of a
 * session. Everything else in the menu is a *feature*, and it renders here only
 * when the brand both enables it and puts it in this slot, wearing whatever
 * that brand calls it. With no profile that is Theme and Clubs & access, which
 * is what the menu has always held.
 *
 * A feature reaches the menu only if somebody also supplied a way to run it
 * (`actions`). The two conditions are separate on purpose: the brand decides
 * *where* a feature belongs, and the host decides *whether it can act* — and a
 * row that renders with nothing behind it is the "control that fails when
 * clicked" this file already refuses to draw.
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { authConfigured, onAuthChanged, signOut, signedInEmail } from "../lib/session";
import { accountLabel, accountTitle, fetchMe, type Me } from "../api/me";
import { plans } from "../api/billing";
import { buyLabel, heldPlanName } from "../lib/billing";
import { actingAs, setActingAs } from "../lib/act-as";
import { QF_US, currentBackend, setBackend } from "../api/backend";
import { BRAND } from "../lib/brand-current";
import type { FeatureId } from "../lib/brand-registry";
import { guideHref } from "../lib/guide";
import { sizingEnabled } from "../api/sizing";
import { aiUsageOffered } from "../api/usage";
import { AiUsage } from "./AiUsage";
import { WhatsNew } from "./WhatsNew";
import { hasUnread, markSeen, notesFor } from "../lib/release-notes";
import { DraggableDialog } from "./DraggableDialog";

export function AppMenu({
  onOpenSettings,
  onOpenAdmin,
  onOpenDataHealth,
  onOpenPlan,
  onOpenGroupRequest,
  onOpenBooks,
  onStartTour,
  actions,
}: {
  onOpenSettings: () => void;
  /** Opens the clubs-and-grants screen. Offered only when the server says admin. */
  onOpenAdmin: () => void;
  /**
   * Opens the fleet's DQHealth — what every machine holds, and whether they
   * agree. Beside the admin screen and gated the same way, because it reads a
   * report about the servers rather than anything a user owns.
   */
  onOpenDataHealth: () => void;
  /** Opens the read-only "what does my plan include" screen. Offered to everyone. */
  onOpenPlan: () => void;
  /**
   * Opens the group request form (`docs/plans/group-requests.md`). Offered to
   * every signed-in account; absent on a shell that has not wired it.
   */
  onOpenGroupRequest?: () => void;
  /**
   * Opens Books — broker imports and the books they make. Offered when the
   * server is configured and the plan includes `feat:sizing`; absent on a
   * shell that has not wired it.
   */
  onOpenBooks?: () => void;
  /**
   * Replays the product tour.
   *
   * Optional, and the row is absent without it: the tour points at the chart
   * toolbar and the screener, so a shell that has neither — the narrow-screen
   * one — should not offer it rather than offer a walkthrough that skips most
   * of itself.
   */
  onStartTour?: () => void;
  /**
   * How to run a feature the brand may have placed in this menu.
   *
   * Only needed for features whose surface lives elsewhere — the journal's
   * toggle and the layout panel are the host's to open, so a brand that moves
   * either into the menu needs the host to pass the handler here. Theme and
   * admin are already props above and are wired without this.
   *
   * A feature placed here with no entry renders nothing rather than a dead row.
   */
  actions?: Partial<Record<FeatureId, () => void>>;
}) {
  const [open, setOpen] = useState(false);
  const [uid, setUid] = useState<string | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [usageOpen, setUsageOpen] = useState(false);
  // What's new: the notes this reader may see, and a dot until they are opened.
  const [newsOpen, setNewsOpen] = useState(false);
  const notes = notesFor(BRAND.key, !!me?.isAdmin);
  const [unread, setUnread] = useState(() => hasUnread(notesFor(BRAND.key, false)));
  useEffect(() => setUnread(hasUnread(notes)), [me?.isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps
  const hostRef = useRef<HTMLDivElement>(null);

  /**
   * Everything this menu knows how to run, keyed by feature.
   *
   * The two it owns outright come from its own props; anything else the host
   * has offered is merged in. `admin` is not here — it is answered by the
   * server (`me.isAdmin`) as well as by the brand, so it keeps its own block
   * below rather than being folded into a list that only asks the brand.
   */
  const runnable: Partial<Record<FeatureId, () => void>> = {
    themes: onOpenSettings,
    ...actions,
  };

  /** A feature renders here when the brand put it here and we can run it. */
  const placedHere = (id: FeatureId): boolean =>
    BRAND.enabled(id) && BRAND.slotOf(id, "desktop") === "appMenu";

  const menuFeatures = BRAND.inSlot("desktop", "appMenu").filter(
    (feature) => runnable[feature.id] !== undefined,
  );

  useEffect(() => onAuthChanged(setUid), []);

  // Everything about the account comes from the server: the stable key that
  // every row in the database is keyed on, whether this caller holds `admin`,
  // and which accounts it may act as. A client that decided any of that for
  // itself would offer a switcher that 403s.
  useEffect(() => {
    if (!uid) {
      setMe(null);
      return;
    }
    let alive = true;
    void fetchMe().then((found) => {
      if (alive) setMe(found);
    });
    return () => {
      alive = false;
    };
  }, [uid]);

  // **The held plan, read as the menu opens** — a payment or a cancel lands by
  // webhook, so a value kept from page load goes stale. `/billing/plans` is
  // cached on the server; null with billing off, and the line shows no plan.
  // The session's email stands in for a server that does not send one yet.
  const [planName, setPlanName] = useState<string | null>(null);
  const [buy, setBuy] = useState<string | null>(null);
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  useEffect(() => {
    if (!open || !uid) return;
    let alive = true;
    void plans().then((answer) => {
      if (!alive) return;
      setPlanName(heldPlanName(answer));
      setBuy(buyLabel(answer));
    });
    void signedInEmail().then((found) => {
      if (alive) setSessionEmail(found);
    });
    return () => {
      alive = false;
    };
  }, [open, uid]);

  // Anything outside dismisses it, and so does Escape — a menu that covers the
  // chart must always be one gesture from gone.
  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      if (!hostRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("pointerdown", away);
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("pointerdown", away);
      window.removeEventListener("keydown", key);
    };
  }, [open]);

  return (
    <div ref={hostRef} style={{ position: "relative", flex: "0 0 auto" }}>
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        title="Menu"
        aria-label="Menu"
        aria-haspopup="menu"
        aria-expanded={open}
        style={TRIGGER}
      >
        ☰
        {unread && <span aria-label="New release notes" style={DOT} data-whats-new-dot />}
      </button>

      {open && (
        <div role="menu" style={PANEL}>
          {authConfigured && (
            <>
              <div style={SECTION}>Account</div>
              <div style={WHO} title={accountTitle(me)}>
                {me
                  ? accountLabel({ ...me, email: me.email ?? sessionEmail })
                  : uid
                    ? "Signed in"
                    : "Not signed in"}
                {me && planName && ` · ${planName}`}
              </div>
              {/* Only when there is something to buy; it opens Your plan…,
                  where the cards are (owner's mock, 2026-10-31). */}
              {me && buy && (
                <button
                  type="button"
                  style={BUY}
                  onClick={() => {
                    onOpenPlan();
                    setOpen(false);
                  }}
                >
                  {buy}
                </button>
              )}
              {/* A Summary user's own charges. On the server's answer, as
                  every gated row here is — `/ai-usage/me` refuses the rest. */}
              {aiUsageOffered(me) && (
                <button
                  type="button"
                  style={ITEM}
                  onClick={() => {
                    setUsageOpen(true);
                    setOpen(false);
                  }}
                >
                  AI usage…
                </button>
              )}
            </>
          )}

          {me?.isAdmin && me.testUsers.length > 0 && (
            <>
              <div style={SECTION}>View as</div>
              <select
                value={
                  me.testUsers.includes(me.actingAs ?? actingAs() ?? "")
                    ? (me.actingAs ?? actingAs() ?? "")
                    : ""
                }
                onChange={(event) => {
                  setActingAs(event.target.value || null);
                  // Every open surface loads on mount and all of them are now
                  // showing the wrong account's data. See ActingAs.
                  window.location.reload();
                }}
                style={SELECT}
              >
                <option value="">myself</option>
                {me.testUsers.map((key) => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                ))}
              </select>
            </>
          )}

          {/* Author as: a group's own account, where its layouts, screens and
              chart templates are authored and published from. Apart from View
              as because the two mean different things — one reproduces what a
              person sees, the other *is* the group — and the banner says which
              (`docs/plans/group-accounts.md` §1). Same switch underneath. */}
          {/* Not gated on `isAdmin`: a Group Admin is not one, and the server
              already lists only the groups this person may author for — every
              group for a platform admin, their own for a Group Admin, none for
              anybody else. */}
          {me && me.groupAccounts.length > 0 && (
            <>
              <div style={SECTION}>Author as</div>
              <select
                aria-label="Author as"
                value={
                  me.groupAccounts.some((g) => g.key === (me.actingAs ?? actingAs()))
                    ? (me.actingAs ?? actingAs() ?? "")
                    : ""
                }
                onChange={(event) => {
                  setActingAs(event.target.value || null);
                  // View as's reason: every surface loaded on mount.
                  window.location.reload();
                }}
                style={SELECT}
              >
                <option value="">myself</option>
                {me.groupAccounts.map((group) => (
                  <option key={group.key} value={group.key}>
                    {group.name}
                  </option>
                ))}
              </select>
            </>
          )}

          {me?.isAdmin && (
            <>
              <div style={SECTION}>Backend</div>
              <select
                value={currentBackend()}
                onChange={(event) => {
                  setBackend(event.target.value);
                  // Reloaded for `ActingAs`'s reason, which applies with more
                  // force here: every open surface loaded its data on mount and
                  // all of it now belongs to a different service. The base URLs
                  // are read once at module load, so nothing short of a reload
                  // would move them anyway.
                  window.location.reload();
                }}
                style={SELECT}
              >
                <option value="">this origin (dev proxy)</option>
                <option value={QF_US}>qf-us — sjc</option>
              </select>
              <div style={WHO}>{currentBackend() || "relative"}</div>
            </>
          )}

          {me?.isAdmin && placedHere("admin") && (
            <>
              <div style={SECTION}>{BRAND.label("admin")}</div>
              {/* Offered on the server's answer, never on a client belief —
                  the same rule as the acting-as switcher above it. `/admin/*`
                  refuses a non-admin anyway, so a menu item shown to everyone
                  would be a control that fails when clicked. */}
              <button
                type="button"
                style={ITEM}
                onClick={() => {
                  onOpenAdmin();
                  setOpen(false);
                }}
              >
                Clubs &amp; access…
              </button>
              <button
                type="button"
                style={ITEM}
                onClick={() => {
                  onOpenDataHealth();
                  setOpen(false);
                }}
              >
                Data health…
              </button>
            </>
          )}

          {onOpenBooks && sizingEnabled() && (
            <>
              <div style={SECTION}>Trading</div>
              <button
                type="button"
                style={ITEM}
                onClick={() => {
                  onOpenBooks();
                  setOpen(false);
                }}
              >
                Books &amp; imports…
              </button>
            </>
          )}

          {/* Anyone may ask (owner, 2026-10-12) — no tier gate, so no
              server answer to wait on before offering it. */}
          {onOpenGroupRequest && (
            <>
              <div style={SECTION}>Groups</div>
              <button
                type="button"
                style={ITEM}
                onClick={() => {
                  onOpenGroupRequest();
                  setOpen(false);
                }}
              >
                Request a group…
              </button>
            </>
          )}

          <div style={SECTION}>Settings</div>
          {/* Everyone, not only an admin: this is the screen that answers "why
              can I not see that", and the person asking is the one without the
              capability. Account chrome rather than a feature — an app whose
              brand had removed the page explaining what you pay for would be
              worse for exactly the people who need it. */}
          <button
            type="button"
            style={ITEM}
            onClick={() => {
              onOpenPlan();
              setOpen(false);
            }}
          >
            Your plan…
          </button>
          {/* Theme was written out here until the brand decided it. The ellipsis
              is this menu's, not the label's: every row that opens something
              carries one, and a brand renaming the feature should not have to
              remember to type it. */}
          {menuFeatures.map((feature) => (
            <button
              key={feature.id}
              type="button"
              style={ITEM}
              onClick={() => {
                runnable[feature.id]?.();
                setOpen(false);
              }}
            >
              {feature.label}…
            </button>
          ))}

          <div style={SECTION}>Help</div>
          <button
            type="button"
            style={ITEM}
            onClick={() => {
              markSeen(notes);
              setUnread(false);
              setNewsOpen(true);
              setOpen(false);
            }}
          >
            What's new…{unread && <span aria-hidden style={{ ...DOT, position: "static", marginLeft: 6 }} />}
          </button>
          {onStartTour && (
            <button
              type="button"
              style={ITEM}
              onClick={() => {
                onStartTour();
                setOpen(false);
              }}
            >
              Take the tour
            </button>
          )}
          {/* **An anchor, not a button that calls `window.open`.** Some browsers
              refuse a programmatic `window.open` outright — the same refusal
              that bit the SciChart licence wizard — and a menu row that does
              nothing is worse than one that opens a tab the reader did not
              expect. A real link also gets middle-click and "open in new tab"
              for free.

              The guide is shipped inside the build at `/guide/index.html` by
              `scripts/copy-guide.mjs`, so this reaches no other host and works
              for every brand. Other surfaces link to one section with
              `guideHref("track-lists")`; this row is the whole thing. */}
          <a
            href={guideHref()}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...ITEM, display: "block", textDecoration: "none" }}
            onClick={() => setOpen(false)}
          >
            User guide…
          </a>

          {uid && (
            <button type="button" style={ITEM} onClick={() => void signOut()}>
              Sign out
            </button>
          )}
        </div>
      )}

      {/* Held here rather than in each shell: the dialog belongs to nobody but
          this row, and threading an `onOpen…` through both shells buys nothing.
          Portalled so no ancestor of the menu can clip or offset it. */}
      {newsOpen &&
        createPortal(
          <DraggableDialog title="What's new" onClose={() => setNewsOpen(false)}>
            <WhatsNew notes={notes} />
          </DraggableDialog>,
          document.body,
        )}
      {usageOpen &&
        createPortal(
          <DraggableDialog title="AI usage" onClose={() => setUsageOpen(false)}>
            <AiUsage />
          </DraggableDialog>,
          document.body,
        )}
    </div>
  );
}

const TRIGGER: React.CSSProperties = {
  position: "relative",
  padding: "0.1rem 0.45rem",
  background: "transparent",
  border: "1px solid transparent",
  borderRadius: 4,
  color: "var(--qf-text-secondary)",
  font: "inherit",
  fontSize: "0.9rem",
  lineHeight: 1.2,
  cursor: "pointer",
};

const DOT: React.CSSProperties = {
  position: "absolute",
  top: 1,
  right: 1,
  display: "inline-block",
  width: 7,
  height: 7,
  borderRadius: "50%",
  background: "var(--qf-accent)",
};

const PANEL: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  minWidth: 260,
  padding: "0.5rem",
  display: "flex",
  flexDirection: "column",
  gap: "0.35rem",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
  // Above the chart toolbars, below the symbol dialog's scrim.
  zIndex: 1000,
};

const SECTION: React.CSSProperties = {
  fontSize: "0.64rem",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--qf-text-muted)",
  borderTop: "1px solid var(--qf-border)",
  paddingTop: "0.35rem",
  // The first section has a rule above it that divides it from nothing.
  marginTop: "0.1rem",
};

const WHO: React.CSSProperties = {
  fontSize: "0.74rem",
  color: "var(--qf-text-secondary)",
  wordBreak: "break-all",
};

const SELECT: React.CSSProperties = {
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  font: "inherit",
  fontSize: "0.78rem",
  padding: "0.25rem 0.3rem",
};

/** The buy button: an item in the accent, so it reads as the way to buy. */
const BUY: React.CSSProperties = {
  padding: "0.3rem 0.5rem",
  background: "var(--qf-accent)",
  border: "1px solid var(--qf-accent)",
  borderRadius: 4,
  color: "var(--qf-background)",
  font: "inherit",
  fontSize: "0.78rem",
  fontWeight: 600,
  cursor: "pointer",
  textAlign: "left",
};

const ITEM: React.CSSProperties = {
  padding: "0.3rem 0.5rem",
  background: "var(--qf-surface-alt)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  color: "var(--qf-text)",
  font: "inherit",
  fontSize: "0.78rem",
  cursor: "pointer",
  textAlign: "left",
};
